import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import { BtnDefault } from "../../../../../../components/buttons/BtnDefault";
import { InputDefault } from "../../../../../../components/forms/InputDefault";
import { PppoeUserService } from "../../../../../../services/PppoeUserService";

export const PppoeProfileUpdate = ({
  data,
  open,
  setOpen,
  refreshData,
}) => {
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    internet_number: "",
    name: "",
    phone_number: "",
  });

  const [error, setError] = useState({
    internet_number: "",
    name: "",
    phone_number: "",
  });

  useEffect(() => {
    if (data && open) {
      setFormData({
        internet_number: data?.user?.internet_number || "",
        name: data?.user?.name || "",
        phone_number: data?.user?.phone_number || "",
      });
      setError({
        internet_number: "",
        name: "",
        phone_number: "",
      });
    }
  }, [data, open]);

  /* ── Hanya angka, maks 10 digit ── */
  const handleInternetNumberChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);

    setFormData((prev) => ({ ...prev, internet_number: raw }));
    setError((prev) => ({ ...prev, internet_number: "" }));
  };

  /* ── Normalisasi nomor HP Indonesia ── */
  const handlePhoneChange = (e) => {
    const raw = e.target.value;

    // 1. Hapus semua yang bukan digit dan bukan + di posisi pertama
    let cleaned = raw.replace(/[^0-9+]/g, "");

    // 2. Pastikan + hanya di depan, dan cuma satu
    if (cleaned.startsWith("+")) {
      cleaned = "+" + cleaned.replace(/\+/g, "").slice(0, 15);
    } else {
      cleaned = cleaned.slice(0, 15);
    }

    // 3. Konversi +62 atau 62 → 0
    if (cleaned.startsWith("+62")) {
      cleaned = "0" + cleaned.slice(3);
    } else if (cleaned.startsWith("62") && !cleaned.startsWith("0")) {
      cleaned = "0" + cleaned.slice(2);
    }

    // 4. Kalau ada angka di depan tapi bukan 0, tambahkan 0
    if (cleaned.length > 0 && !cleaned.startsWith("0")) {
      cleaned = "0" + cleaned;
    }

    // 5. Final cleanup: pastikan murni digit (setelah konversi, + sudah hilang)
    cleaned = cleaned.replace(/[^0-9]/g, "").slice(0, 15);

    setFormData((prev) => ({ ...prev, phone_number: cleaned }));
    setError((prev) => ({ ...prev, phone_number: "" }));
  };

  const handleChange = (e) => {
    const { id, value } = e.target;

    setFormData((prev) => ({ ...prev, [id]: value }));
    setError((prev) => ({ ...prev, [id]: "" }));
  };

  /* ── Validasi lokal sebelum kirim ── */
  const validateLocal = () => {
    const errs = { internet_number: "", name: "", phone_number: "" };
    let valid = true;

    // Internet Number: wajib, tepat 10 digit angka
    if (!formData.internet_number) {
      errs.internet_number = "ID Pelanggan wajib diisi";
      valid = false;
    } else if (!/^[0-9]{10}$/.test(formData.internet_number)) {
      errs.internet_number = "ID Pelanggan harus tepat 10 digit angka";
      valid = false;
    }

    // Name: wajib
    if (!formData.name.trim()) {
      errs.name = "Nama pelanggan wajib diisi";
      valid = false;
    }

    // Phone: kalau diisi, harus valid
    if (formData.phone_number) {
      if (!/^0[0-9]{9,14}$/.test(formData.phone_number)) {
        errs.phone_number = "Format nomor HP tidak valid";
        valid = false;
      }
    }

    setError(errs);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateLocal()) return;

    setLoading(true);

    try {
      await PppoeUserService.profileUpdate({
        data: formData,
        id: data.id,
      });

      refreshData?.();
      setOpen(false);
    } catch (err) {
      if (err.response?.data?.errors) {
        const serverErrors = err.response.data.errors;
        setError((prev) => ({
          internet_number: serverErrors.internet_number?.[0] || prev.internet_number,
          name: serverErrors.name?.[0] || prev.name,
          phone_number: serverErrors.phone_number?.[0] || prev.phone_number,
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-slate-800 w-full max-w-lg rounded-2xl shadow-2xl overflow-visible">

        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="text-lg font-bold text-white">
            Update Profil Pelanggan
          </h3>

          <button
            onClick={() => setOpen(false)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <FiX size={20} className="text-white" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">

          <div>
            <InputDefault
              id="internet_number"
              label="ID Pelanggan"
              type="text"
              inputMode="numeric"
              placeholder="Masukkan 10 digit ID Pelanggan"
              value={formData.internet_number}
              onChange={handleInternetNumberChange}
              error={error.internet_number}
            />
            <p className="text-[11px] text-slate-500 mt-1 ml-1">
              {formData.internet_number.length}/10 digit
            </p>
          </div>

          <InputDefault
            id="name"
            label="Nama"
            type="text"
            placeholder="Masukkan nama pelanggan"
            value={formData.name}
            onChange={handleChange}
            error={error.name}
          />

          <div>
            <InputDefault
              id="phone_number"
              label="No Telepon"
              type="text"
              inputMode="tel"
              placeholder="08xxxxxxxxxx"
              value={formData.phone_number}
              onChange={handlePhoneChange}
              error={error.phone_number}
            />
            <p className="text-[11px] text-slate-500 mt-1 ml-1">
              Format: 08xx, +62xx, atau 62xx — otomatis dikonversi ke 0xx
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t border-white/10">
            <button
              type="button"
              disabled={loading}
              onClick={() => setOpen(false)}
              className="flex-1 px-4 py-2.5 border border-white text-white rounded-xl font-semibold hover:bg-white/10 disabled:opacity-50 transition-colors"
            >
              Batal
            </button>

            <BtnDefault
              type="submit"
              disabled={loading}
              className="flex-1 justify-center py-2.5"
            >
              {loading ? "Sedang Menyimpan..." : "Simpan"}
            </BtnDefault>
          </div>
        </form>
      </div>
    </div>
  );
};